import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { CustomerGroupType } from "@redotech/redo-customer-segments-common/customers/customer-group-definition";
import {
  CustomerSegmentOperation,
  ManageStaticSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import UsersIcon from "@redotech/redo-icons/users-01.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { useMemo } from "react";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface ManageStaticSegmentNodeData {
  deleteElements: (id: string) => void;
  step: PartialWithRequiredType<ManageStaticSegmentStep>;
}

export function ManageStaticSegmentNode(data: {
  id: string;
  data: ManageStaticSegmentNodeData;
  selected?: boolean;
}) {
  const { t } = useTranslation();
  const client = useRequiredContext(RedoMarketingRpcClientContext);

  const { operation, segmentId } = data.data.step;

  const segmentsLoad = useLoad(
    async (signal) =>
      client.getCustomerGroupsMinimal(
        { type: CustomerGroupType.STATIC },
        { signal },
      ),
    [],
  );

  const foundSegment = useMemo(
    () => segmentsLoad.value?.find((s) => s._id === segmentId),
    [segmentId, segmentsLoad.value],
  );

  const segmentName =
    foundSegment?.name || segmentId || t("No segment selected");

  const operationLabel =
    operation === CustomerSegmentOperation.ADD
      ? t("Add to segment")
      : t("Remove from segment");

  return (
    <>
      <Handle position={Position.Top} type="target" />
      <BaseNode
        icon={<RedoFeaturedIcon color="success" Icon={UsersIcon} size="xs" />}
        onDelete={() => data.data.deleteElements(data.id)}
        selected={data.selected}
        step={data.data.step}
        title={t("Manage static segments")}
      >
        <Flex dir="column" gap="xs">
          <Text fontSize="xxs">{operationLabel}</Text>
          <Text className={NodeCSS.valueText}>{segmentName}</Text>
        </Flex>
      </BaseNode>
      <Handle position={Position.Bottom} type="source" />
    </>
  );
}
